import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  Discount,
  ListDiscountsInput,
  ListDiscountsResponse,
  GetListPricesInput,
  GetListPricesResponse,
  GetAvailableCategoriesResponse,
  ListPriceResult,
  DeleteDiscountInput,
} from '@erp/rpc';

/**
 * Hook for fetching discounts with optional filters
 */
export function useDiscountsList(filters: ListDiscountsInput = {}) {
  const query = useQuery(
    orpc.accountsReceivable.discounts.list.queryOptions({
      input: filters,
    }),
  );

  const discounts = query.data?.discounts ?? [];
  const pagination = query.data?.pagination;
  // React Query v5 can briefly have status='pending' with isFetching=false
  // during initialization. Use isPendingOrLoading to ensure loading state
  // is shown until data actually arrives.
  const isPendingOrLoading = query.isLoading || query.status === 'pending';

  return {
    ...query,
    isPendingOrLoading,
    discounts,
    pagination,
  } as typeof query & {
    data?: ListDiscountsResponse;
    discounts: Discount[];
    pagination: ListDiscountsResponse['pagination'] | undefined;
    isPendingOrLoading: boolean;
  };
}

/**
 * Hook for fetching list prices for SKUs (single customer)
 */
export function useListPrices(input: GetListPricesInput | null) {
  const query = useQuery({
    ...orpc.accountsReceivable.discounts.getListPrices.queryOptions({
      input: input ?? { customerId: '', skus: [] },
    }),
    enabled: !!input && input.skus.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes on client side
  });

  const prices = query.data?.prices ?? {};
  // Use isFetching for loading state (covers both initial and refetch)
  const isLoadingPrices = query.isFetching;

  return {
    ...query,
    prices,
    isLoadingPrices,
  } as typeof query & {
    data?: GetListPricesResponse;
    prices: GetListPricesResponse['prices'];
    isLoadingPrices: boolean;
  };
}

/**
 * Input for multi-customer list price fetching
 */
export interface CustomerSkusInput {
  customerId: string;
  skus: string[];
}

/**
 * Result type for multi-customer list prices
 */
export type ListPricesMap = Map<string, ListPriceResult | null>;

/**
 * Helper to create a unique key for customer+sku combination
 */
function makeListPriceKey(customerId: string, sku: string): string {
  return `${customerId}:${sku}`;
}

/**
 * Hook for fetching list prices for multiple customers in parallel.
 * Each customer's prices are fetched in a separate query and cached independently.
 * Results are merged into a single Map keyed by "customerId:sku".
 */
export function useMultiCustomerListPrices(inputs: CustomerSkusInput[]) {
  // Create stable query keys by filtering out empty inputs
  const validInputs = useMemo(
    () => inputs.filter((i) => i.customerId && i.skus.length > 0),
    [inputs],
  );

  // Use useQueries to fetch all customers in parallel
  const queries = useQueries({
    queries: validInputs.map((input) => ({
      queryKey: [
        'listPrices',
        input.customerId,
        input.skus.sort().join(','),
      ] as const,
      queryFn: async () => {
        const result =
          await rpcClient.accountsReceivable.discounts.getListPrices({
            customerId: input.customerId,
            skus: input.skus,
          });
        return { customerId: input.customerId, prices: result.prices };
      },
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      enabled: input.skus.length > 0,
    })),
  });

  // Merge all results into a single map
  const listPrices: ListPricesMap = useMemo(() => {
    const map = new Map<string, ListPriceResult | null>();

    for (const query of queries) {
      if (query.data) {
        const { customerId, prices } = query.data;
        for (const [sku, priceData] of Object.entries(prices)) {
          const key = makeListPriceKey(customerId, sku);
          map.set(key, priceData);
        }
      }
    }

    return map;
  }, [queries]);

  // Overall loading state - true if any query is fetching
  const isLoading = queries.some((q) => q.isFetching);

  // Check if all queries have completed at least once
  const isInitialLoading = queries.some(
    (q) => q.status === 'pending' && !q.isFetched,
  );

  return {
    listPrices,
    isLoading,
    isInitialLoading,
    queries,
  };
}

/**
 * Hook for fetching available product categories and product lines for discount targeting
 */
export function useAvailableCategories() {
  const query = useQuery(
    orpc.accountsReceivable.discounts.getAvailableCategories.queryOptions({}),
  );

  const categories = query.data?.categories ?? [];
  const productLines = query.data?.productLines ?? [];

  return {
    ...query,
    categories,
    productLines,
  } as typeof query & {
    data?: GetAvailableCategoriesResponse;
    categories: string[];
    productLines: string[];
  };
}

/**
 * Hook for deleting a discount
 */
export function useDeleteDiscount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeleteDiscountInput) => {
      return rpcClient.accountsReceivable.discounts.delete(input);
    },
    onSuccess: () => {
      // Invalidate the discounts list to refetch
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.discounts.list.queryKey({
          input: {},
        }),
      });
    },
  });
}
